/**
 * 循环容器配置面板
 *
 * 配置章节范围、迭代步数和循环反馈
 */

import React, { useState, useCallback, useEffect } from 'react';
import { clsx } from 'clsx';
import type { LoopNodeData } from '../../../types/workflow';
import { useWorkflowStore } from '../../../store/workflowStore';

export interface LoopNodeConfigPanelProps {
  /** 节点 ID */
  nodeId: string;
}

export const LoopNodeConfigPanel: React.FC<LoopNodeConfigPanelProps> = React.memo(({
  nodeId,
}) => {
  // 从 store 获取节点数据
  const node = useWorkflowStore((state) =>
    state.nodes.find((n) => n.id === nodeId)
  );
  const updateNodeForm = useWorkflowStore((state) => state.updateNodeForm);

  // 本地表单状态
  const [formData, setFormData] = useState<{
    label: string;
    chapter_start: number;
    chapter_end: number;
    step: number;
    loop_feedback: boolean;
  }>({
    label: '',
    chapter_start: 1,
    chapter_end: 10,
    step: 1,
    loop_feedback: false,
  });

  // 错误状态
  const [errors, setErrors] = useState<{
    chapter_range?: string;
    step?: string;
  }>({});

  // 同步节点数据到本地状态
  useEffect(() => {
    if (node) {
      const data = node.data as LoopNodeData;
      setFormData({
        label: data.label || '循环容器',
        chapter_start: data.chapter_start || 1,
        chapter_end: data.chapter_end || 10,
        step: data.step || 1,
        loop_feedback: data.loop_feedback || false,
      });
    }
  }, [node]);

  // 验证表单
  const validateForm = useCallback(() => {
    const newErrors: typeof errors = {};

    if (formData.chapter_start > formData.chapter_end) {
      newErrors.chapter_range = '起始章节不能大于结束章节';
    }

    if (formData.chapter_start < 1) {
      newErrors.chapter_range = '起始章节必须大于 0';
    }

    if (formData.step < 1) {
      newErrors.step = '迭代步数必须大于 0';
    }

    const maxStep = formData.chapter_end - formData.chapter_start + 1;
    if (formData.step > maxStep) {
      newErrors.step = `迭代步数不能超过章节范围 (${maxStep})`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // 保存配置
  const handleSave = useCallback(() => {
    if (!validateForm()) {
      return;
    }

    updateNodeForm(nodeId, {
      label: formData.label,
      chapter_start: formData.chapter_start,
      chapter_end: formData.chapter_end,
      step: formData.step,
      loop_feedback: formData.loop_feedback,
    });
  }, [nodeId, formData, updateNodeForm, validateForm]);

  // 更新字段
  const updateField = useCallback(<K extends keyof typeof formData>(
    field: K,
    value: (typeof formData)[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // 清除对应错误
    if (errors[field as keyof typeof errors]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as keyof typeof errors];
        return next;
      });
    }
  }, [errors]);

  // 计算迭代次数
  const totalChapters = Math.max(0, formData.chapter_end - formData.chapter_start + 1);
  const iterationCount = formData.step > 0 ? Math.ceil(totalChapters / formData.step) : 0;

  return (
    <div className="p-4 space-y-4">
      {/* 节点名称 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          节点名称
        </label>
        <input
          type="text"
          value={formData.label}
          onChange={(e) => updateField('label', e.target.value)}
          placeholder="循环容器"
          className={clsx(
            'w-full px-3 py-2 rounded-md border border-gray-200',
            'focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent',
            'text-sm text-gray-700',
          )}
        />
      </div>

      {/* 章节范围 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          章节范围 <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <label className="text-xs text-gray-500 mb-1 block">起始章节</label>
            <input
              type="number"
              min={1}
              value={formData.chapter_start}
              onChange={(e) => updateField('chapter_start', parseInt(e.target.value) || 1)}
              onBlur={validateForm}
              className={clsx(
                'w-full px-3 py-2 rounded-md border',
                'focus:outline-none focus:ring-2 focus:border-transparent',
                'text-sm text-gray-700',
                errors.chapter_range
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-200 focus:ring-purple-500',
              )}
            />
          </div>
          <span className="text-gray-400 pt-5">-</span>
          <div className="flex-1">
            <label className="text-xs text-gray-500 mb-1 block">结束章节</label>
            <input
              type="number"
              min={1}
              value={formData.chapter_end}
              onChange={(e) => updateField('chapter_end', parseInt(e.target.value) || 1)}
              onBlur={validateForm}
              className={clsx(
                'w-full px-3 py-2 rounded-md border',
                'focus:outline-none focus:ring-2 focus:border-transparent',
                'text-sm text-gray-700',
                errors.chapter_range
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-200 focus:ring-purple-500',
              )}
            />
          </div>
        </div>
        {errors.chapter_range && (
          <p className="mt-1 text-sm text-red-500">{errors.chapter_range}</p>
        )}
      </div>

      {/* 迭代步数 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          迭代步数 <span className="text-red-500">*</span>
        </label>
        <input
          type="number"
          min={1}
          max={totalChapters}
          value={formData.step}
          onChange={(e) => updateField('step', parseInt(e.target.value) || 1)}
          onBlur={validateForm}
          className={clsx(
            'w-full px-3 py-2 rounded-md border',
            'focus:outline-none focus:ring-2 focus:border-transparent',
            'text-sm text-gray-700',
            errors.step
              ? 'border-red-300 focus:ring-red-500'
              : 'border-gray-200 focus:ring-purple-500',
          )}
        />
        {errors.step && (
          <p className="mt-1 text-sm text-red-500">{errors.step}</p>
        )}
        <p className="mt-1.5 text-xs text-gray-400">
          每次循环处理的章节数，共 {totalChapters} 章，将执行 {iterationCount} 轮循环
        </p>
      </div>

      {/* 循环反馈开关 */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-medium text-gray-700">
              循环反馈
            </label>
            <p className="text-xs text-gray-400 mt-0.5">
              启用后，每次循环的结果会传递给下一次循环
            </p>
          </div>
          <button
            onClick={() => updateField('loop_feedback', !formData.loop_feedback)}
            className={clsx(
              'relative w-11 h-6 rounded-full transition-colors',
              formData.loop_feedback ? 'bg-purple-500' : 'bg-gray-200',
            )}
            role="switch"
            aria-checked={formData.loop_feedback}
          >
            <span
              className={clsx(
                'absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform',
                formData.loop_feedback && 'translate-x-5',
              )}
            />
          </button>
        </div>
      </div>

      {/* 循环变量说明 */}
      <div className="p-3 bg-purple-50 rounded-md">
        <div className="flex items-center gap-2 text-sm font-medium text-purple-700 mb-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          循环变量
        </div>
        <div className="space-y-1 text-xs text-gray-500">
          <div className="flex items-center gap-2">
            <code className="px-1.5 py-0.5 bg-purple-100 rounded text-purple-600">{'{loop.iteration_index}'}</code>
            <span>当前迭代索引 (0-based)</span>
          </div>
          <div className="flex items-center gap-2">
            <code className="px-1.5 py-0.5 bg-purple-100 rounded text-purple-600">{'{loop.current_chapter}'}</code>
            <span>当前章节号</span>
          </div>
          <div className="flex items-center gap-2">
            <code className="px-1.5 py-0.5 bg-purple-100 rounded text-purple-600">{'{loop.chapter_start}'}</code>
            <span>本轮起始章节</span>
          </div>
          <div className="flex items-center gap-2">
            <code className="px-1.5 py-0.5 bg-purple-100 rounded text-purple-600">{'{loop.chapter_end}'}</code>
            <span>本轮结束章节</span>
          </div>
          <div className="flex items-center gap-2">
            <code className="px-1.5 py-0.5 bg-purple-100 rounded text-purple-600">{'{loop_feedback}'}</code>
            <span>上一次循环的输出（需启用）</span>
          </div>
        </div>
      </div>

      {/* 子节点信息 */}
      <div className="p-3 bg-gray-50 rounded-md">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          子节点操作
        </div>
        <p className="text-xs text-gray-500">
          将其他节点拖入循环容器内部，即可将其作为循环执行的子节点。子节点会自动继承当前轮次的章节范围。
        </p>
      </div>

      {/* 保存按钮 */}
      <button
        onClick={handleSave}
        className={clsx(
          'w-full py-2 px-4 rounded-md text-sm font-medium',
          'bg-purple-500 text-white',
          'hover:bg-purple-600 transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2',
        )}
      >
        保存配置
      </button>
    </div>
  );
});

LoopNodeConfigPanel.displayName = 'LoopNodeConfigPanel';

export default LoopNodeConfigPanel;